import { useEffect, useId, useState } from 'react'

import { Label } from 'react-aria-components'
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Button, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { VagaPadraoAcessoriosExtras } from '@/features/proposta/brasil/components/vaga-padrao/vaga-padrao-acessorios-extras'
import { VagaPadraoSelector } from '@/features/proposta/brasil/components/vaga-padrao/vaga-padrao-seletor'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import type { TVagasSelecionadas } from '@/features/proposta/brasil/types/proposta.type'
import type { TProduto } from '@/types/produto'
import { opcaoVagaEnum, type TOpcaoVagaEnum } from '@/types/proposta'

const Step = {
  SELECIONAR_OPCAO: 0,
  DEFINIR_VAGA: 1,
} as const

type TVagaPadraoMultiplosAcessoriosProps = {
  vagas: Array<{
    nome: string
    produtos: TProduto[]
  }>
  acessorios: Array<TPropostaSchemaInput['informacoesIniciais']['simulacoesAcessorios'][number]['produto']>
  onConfirm: (selectedVagas: TVagasSelecionadas) => void
}

export const VagaPadraoEscolhaOpcoes = ({ vagas, acessorios, onConfirm }: TVagaPadraoMultiplosAcessoriosProps) => {
  const id = useId()
  const { t } = useTranslation('proposta')
  const [step, setStep] = useState<(typeof Step)[keyof typeof Step]>(Step.SELECIONAR_OPCAO)
  const { watch, setValue } = useFormContext<FieldValues>()

  const opcaoVagaConfirmada = watch('informacoesIniciais.opcaoVaga' as FieldPath<FieldValues>) as
    | TOpcaoVagaEnum
    | null
    | undefined
  const defaultSelecionadas = watch('informacoesIniciais.vagaIds' as FieldPath<FieldValues>) as TVagasSelecionadas
  const [opcaoSelecionada, setOpcaoSelecionada] = useState<TOpcaoVagaEnum | null | undefined>(opcaoVagaConfirmada)

  const selecionouEscolherVagaPadrao = opcaoSelecionada === opcaoVagaEnum.Padrao
  const selecionouTrocarVagaPorAcessorio = opcaoSelecionada === opcaoVagaEnum.Customizada

  const handleTrocarConteudoNaConfirmacaoOpcao = () => {
    setStep(Step.DEFINIR_VAGA)
    setValue('informacoesIniciais.opcaoVaga', opcaoSelecionada)
    const vagasSelecionadasSaoDeTroca = defaultSelecionadas.some((vaga) => !!vaga.substituiVagaPadrao)

    const escolhaIncompativelComVagasSelecionadas =
      (selecionouEscolherVagaPadrao && vagasSelecionadasSaoDeTroca) ||
      (selecionouTrocarVagaPorAcessorio && !vagasSelecionadasSaoDeTroca)

    if (escolhaIncompativelComVagasSelecionadas) {
      setValue('informacoesIniciais.vagaIds', [])
    }
  }

  const handleGoBack = () => {
    setStep(Step.SELECIONAR_OPCAO)
  }

  const renderizarDeAcordoComOpcaoSelecionada = () => {
    if (selecionouEscolherVagaPadrao) {
      return <VagaPadraoSelector handleGoBack={handleGoBack} vagas={vagas} onConfirm={onConfirm} />
    }
    return (
      <VagaPadraoAcessoriosExtras
        acessorios={acessorios}
        onConfirmar={onConfirm}
        handleGoBack={handleGoBack}
        defaultSelecionadas={defaultSelecionadas}
      />
    )
  }

  useEffect(() => {
    const apenasUmAcessorioExtraNaProposta =
      step === Step.DEFINIR_VAGA && selecionouTrocarVagaPorAcessorio && acessorios.length === 1

    if (apenasUmAcessorioExtraNaProposta) {
      onConfirm([
        {
          id: acessorios[0]!.idExterno,
          nomeVaga: 'Acessorio',
          descricao: acessorios[0]!.codigo,
          substituiVagaPadrao: true,
        },
      ])
    }
  }, [step, acessorios, onConfirm, selecionouTrocarVagaPorAcessorio])

  return (
    <Render.If condition={step === Step.SELECIONAR_OPCAO} fallback={renderizarDeAcordoComOpcaoSelecionada()}>
      {() => (
        <>
          <SheetHeader>
            <SheetTitle variant="action">
              {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.titulo')}
            </SheetTitle>
            <SheetDescription className="max-w-137 text-sm text-neutral-500 sm:text-base">
              {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.descricao')}
            </SheetDescription>
          </SheetHeader>
          <RadioGroup
            value={opcaoSelecionada ?? ''}
            onValueChange={(value: TOpcaoVagaEnum) => setOpcaoSelecionada(value)}
            className="font-nunito flex flex-col gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value={opcaoVagaEnum.Padrao} id={`${id}-${opcaoVagaEnum.Padrao}`} />
              <Label className="text-neutral-700" htmlFor={`${id}-${opcaoVagaEnum.Padrao}`}>
                {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.escolher_vaga_padrao')}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value={opcaoVagaEnum.Customizada} id={`${id}-${opcaoVagaEnum.Customizada}`} />
              <Label className="text-neutral-700" htmlFor={`${id}-${opcaoVagaEnum.Customizada}`}>
                {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.troca_por_acessorio')}
              </Label>
            </div>
          </RadioGroup>
          <SheetFooter variant="sticky">
            <Button
              disabled={opcaoSelecionada === opcaoVagaEnum.Pendente}
              className="w-full justify-self-end aria-disabled:pointer-events-none sm:w-fit"
              onClick={handleTrocarConteudoNaConfirmacaoOpcao}
            >
              {t('informacoes_iniciais.cards.vaga_padrao.trigger.confirmar')}
            </Button>
          </SheetFooter>
        </>
      )}
    </Render.If>
  )
}
